import { gql, useQuery } from "@apollo/client";
import { createRef, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import useGravityForm, { ACTION_TYPES } from "../../../hooks/use-gravity-form";

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
// const RECAPTCHA_SECRET_KEY = process.env.RECAPTCHA_SECRET_KEY;

export const CAPTCHA_FIELDS = gql`
  fragment CaptchaFields on CaptchaField {
    id
    captchaBadgePosition
    captchaLanguage
    captchaTheme
    captchaType
    cssClass
    databaseId
    description
    descriptionPlacement
    displayOnly
    errorMessage
    inputType
    label
    labelPlacement
    layoutGridColumnSpan
    layoutSpacerGridColumnSpan
    pageNumber
    simpleCaptchaBackgroundColor
    simpleCaptchaFontColor
    simpleCaptchaSize
    type
    value
    visibility
  }
`;

const GET_GFORM_SETTINGS = gql`
  query GFORM_SETTINGS {
    gfSettings {
      recaptcha {
        # publicKey
        type
      }
    }
  }
`;

export default function CaptchaField({ field, fieldErrors, ref }) {
  const [isLoaded, setLoaded] = useState(false);
  const recaptchaRef = createRef();

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      const button = document.getElementById("recaptcha-container");
      if (button !== undefined && button !== null) {
        const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible",
          /*
          Motivation for the code:
          1. https://github.com/firebase/firebase-js-sdk/issues/3356
          2. https://github.com/jsardev/reaptcha/issues/218
          3. https://github.com/dozoisch/react-google-recaptcha/issues/129

          Cannot read property 'style' of null was the error message, and to get/test this error do the following steps:
          1. Enter a number, and an invalid number ideally
          2. Keep trying to submit till you see the captcha of image selector where it asks you to select buses, etc
          3. Once you click on Verify, it will show an error screen, and in some cases even when number was valid.

          Since the error was not due to any other code in this file, I decided to reset the captcha in each type of callback, and this seems to have solved the error
          */
          callback: () => {
            recaptchaRef.current.reset();
          },
          "expired-callback": () => {
            recaptchaRef.current.reset();
          },
          "error-callback": () => {
            recaptchaRef.current.reset();
          },
        });
        window.recaptchaVerifier = verifier;
      }
    }
  });

  useEffect(() => {
    if (isLoaded && fieldErrors) {
      recaptchaRef.current.reset();
    }
  }, [fieldErrors, isLoaded]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    const handleSubmitClick = (event) => {
      if (ref.current?.contains(event.target)) {
        handleChange();
      }
    };
    window.addEventListener("mousedown", handleSubmitClick);
    return () => {
      window.removeEventListener("mousedown", handleSubmitClick);
    };
  }, [ref, isLoaded]);

  const { dispatch } = useGravityForm();

  const settings = useQuery(GET_GFORM_SETTINGS);
  if (settings.loading) {
    return;
  }
  const captchaSettings = settings.data?.gfSettings?.recaptcha;

  const {
    id,
    // captchaBadgePosition,
    // captchaLanguage,
    captchaTheme,
    // captchaType,
    // conditionalLogic {
    //     actionType,
    //     logicType,
    //     rules {
    //       fieldId,
    //       operator,
    //       value,
    //   },
    // },
    cssClass,
    databaseId,
    description,
    // descriptionPlacement,
    // displayOnly,
    // errorMessage,
    // inputType,
    label,
    // labelPlacement,
    type,
  } = field;

  const htmlId = `field_${databaseId}_${id}`;

  const handleChange = async () => {
    let recaptchaValue = "";
    if (captchaSettings?.type === "INVISIBLE") {
      recaptchaValue = await recaptchaRef.current.executeAsync();
    } else {
      recaptchaValue = recaptchaRef.current.getValue();
    }

    // console.log(recaptchaValue, databaseId);

    dispatch({
      type: ACTION_TYPES.updateCaptchaFieldValue,
      fieldValue: {
        id: databaseId,
        value: recaptchaValue,
      },
    });
  };

  return (
    <div
      id={htmlId}
      className={`gfield gfield-${type} ${cssClass ?? ""}`.trim()}
    >
      <label htmlFor={`input_${databaseId}_${id}`}>{label}</label>
      <ReCAPTCHA
        onExpired={handleChange}
        onChange={handleChange}
        asyncScriptOnLoad={() => setLoaded(true)}
        ref={recaptchaRef}
        // sitekey={captchaSettings?.publicKey}
        sitekey={RECAPTCHA_SITE_KEY}
        theme={captchaTheme || "light"}
        size={captchaSettings?.type === "INVISIBLE" ? "invisible" : "normal"}
      />
      {description && <p className="field-description">{description}</p>}
      {fieldErrors?.length > 0 &&
        fieldErrors.map((fieldError) => (
          <p key={fieldError.id} className="error-message">
            {fieldError.message}
          </p>
        ))}
    </div>
  );
}
